<template>
  <div class="net-consumption-card">
    <div class="net-consumption-header">
      <div>
        <span class="net-title">{{ title }}</span>
      </div>
      <div class="net-value">
        <span class="net-number">{{ netConsumptionText }}</span>
        <span class="net-unit">Liter</span>
      </div>
    </div>

    <div class="net-chart">
      <div class="net-chart-canvas">
        <ApexChart
          v-if="chartOption && chartOption.series && chartOption.series.length > 0"
          :element="elementId"
          :chartOption="chartOption"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { interpolateNulls } from '@/util/summary/series';

export default {
  name: 'NetMainEngineConsumptionChart',
  props: {
    series: {
      type: Array,
      default: () => [],
    },
    netConsumption: {
      type: [String, Number],
      default: '-',
    },
    title: {
      type: String,
      default: 'NET MAIN ENGINE CONSUMPTION',
    },
    fuelKeys: {
      type: Array,
      default: () => ['ME_FuelCons'],
    },
  },
  data() {
    return {
      elementId: `net-main-cons-${this._uid}`,
    };
  },
  computed: {
    netConsumptionText() {
      return this.formatNumber(this.netConsumption);
    },
    chartOption() {
      if (!this.series || this.series.length === 0) return null;

      const seriesData = this.series.length > 80 ? this.series.slice(-80) : this.series;
      const mappedData = seriesData.map((data) => {
        const fmData = data?.fmData || {};
        let sum = 0;
        let any = false;
        this.fuelKeys.forEach((key) => {
          const v = Number(fmData[key]);
          if (Number.isFinite(v)) {
            sum += v;
            any = true;
          }
        });
        return any ? Number(sum.toFixed(2)) : null;
      });

      const interpolated = interpolateNulls(mappedData);

      const chartSeries = [
        { name: 'Fuel Cons (Interpolated)', data: interpolated },
        { name: 'Fuel Cons', data: mappedData },
      ];

      return {
        series: chartSeries,
        chart: {
          height: 150,
          width: '100%',
          type: 'area',
          sparkline: {
            enabled: true,
          },
          group: 'sparklines',
          toolbar: { show: false },
        },
        markers: {
          size: 0,
          hover: {
            size: 4,
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          width: 2,
          curve: 'smooth',
          dashArray: [4, 0],
        },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.5,
            opacityTo: 0,
          },
        },
        colors: ['rgba(37, 99, 235, 0.4)', '#2563eb'],
        xaxis: {
          type: 'datetime',
          categories: seriesData.map((data) => data.timestamp * 1000),
        },
        tooltip: {
          enabled: true,
          followCursor: true,
          intersect: false,
          enabledOnSeries: [1],
          x: {
            format: 'dd/MM/yy HH:mm',
          },
        },
      };
    },
  },
  methods: {
    formatNumber(value) {
      const num = Number(value);
      if (!Number.isFinite(num)) return '-';
      return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
  },
};
</script>

<style scoped>
.net-consumption-card {
  margin-top: 12px;
  padding: 14px 16px;
  border: 1.5px solid var(--mz-border);
  border-radius: var(--mz-radius-lg);
  background: var(--mz-surface);
}

.net-consumption-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
}

.net-title {
  font-size: var(--mz-fs-md);
  font-weight: 700;
  letter-spacing: 0.8px;
  color: var(--mz-text-muted);
}

.net-value {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.net-number {
  font-size: var(--mz-fs-xl);
  font-weight: 800;
  color: var(--mz-text-strong);
}

.net-unit {
  font-size: var(--mz-fs-sm);
  color: var(--mz-text-subtle);
}

.net-chart {
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 14px;
  background: var(--mz-surface-2);
  border: 1px solid var(--mz-border);
  min-height: 150px;
  display: flex;
  align-items: center;
}

.net-chart-canvas {
  width: 100%;
}

.net-chart-empty {
  font-size: var(--mz-fs-sm);
  color: var(--mz-text-faint);
}
</style>
